import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import type { PortfolioChatDto } from "@/api/types";
import type { ChatReplyPayload } from "@/components/ChatWindow/ChatWindow";
import { ChatWindow } from "@/components/ChatWindow/ChatWindow";
import { useChatReplies } from "@/components/ChatWindow/useChatReplies";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { LogOut01Icon } from "@/components/Icons/LogOut01Icon";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Badge } from "@/design-system/Badge";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Inline } from "@/design-system/Inline";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useSlug } from "@/hooks/useSlug";
import { ProjectLoader } from "@/providers/ProjectLoader";
import { SessionUserLoader } from "@/providers/SessionUserLoader";
import { useChatReplyHandler } from "@/queries/chats/helpers";
import { portfolioChatMutations } from "@/queries/portfolio-chat/mutations";
import { usePortfolioChatQueries } from "@/queries/portfolio-chat/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

type PortfolioGroupChatDetailsModalProps = ModalBaseProps & {
  chatId?: string;
};

export function PortfolioGroupChatDetailsModal({
  isOpened,
  onOpenChange,
  onExitComplete,
  chatId,
}: PortfolioGroupChatDetailsModalProps): React.ReactNode {
  const portfolioChatQueries = usePortfolioChatQueries();
  const chatIdForQuery = chatId ?? "";

  const {
    data: chatDetails,
    isLoading: isLoadingChatDetails,
    error: errorChatDetails,
  } = useQuery({
    ...portfolioChatQueries.chatDetails({ chatId: chatIdForQuery }),
    enabled: Boolean(chatId),
  });
  // Hoist optional accesses into locals. The React Compiler (oxc) strips the
  // `?.` when it lifts a member access into a cache-dependency key, so reading
  // `chatDetails?.id` inline compiles to an unguarded `chatDetails.id` that
  // throws while chatDetails is still undefined. A local keeps the guard.
  const detailsChatId = chatDetails?.id;
  const projectName = chatDetails?.project?.name;
  const { isLoading: isLoadingReplies, error: errorReplies } = useInfiniteQuery({
    ...portfolioChatQueries.listChatRepliesInfinite({ chatId: detailsChatId || "" }),
    enabled: Boolean(chatDetails),
  });

  const isLoading = !chatDetails || isLoadingChatDetails || isLoadingReplies;
  const error = errorChatDetails || errorReplies;

  return (
    <Modal.Root
      title={chatDetails?.groupChatInfo?.name}
      badgeComponent={projectName && <Badge>{projectName}</Badge>}
      size="fullscreen"
      data-testid="portfolio-group-chat-details-modal"
      {...{ isOpened, onOpenChange, onExitComplete }}
    >
      <Modal.Body>
        {isLoading && <FullSizeLoader noDelay />}
        {error && <ErrorPage error={error} />}
        {!isLoading && !error && (
          <ProjectLoader projectId={chatDetails.project.id}>
            <SessionUserLoader>
              <PortfolioGroupChatDetailsContent chat={chatDetails} onClose={() => onOpenChange(false)} />
            </SessionUserLoader>
          </ProjectLoader>
        )}
      </Modal.Body>
    </Modal.Root>
  );
}

type PortfolioGroupChatDetailsContentProps = {
  chat: PortfolioChatDto;
  onClose: () => void;
};

function PortfolioGroupChatDetailsContent({ chat, onClose }: PortfolioGroupChatDetailsContentProps): React.ReactNode {
  const portfolioChatQueries = usePortfolioChatQueries();
  const { t } = useTranslation();
  const slug = useSlug();

  const {
    data: repliesData,
    hasNextPage: hasMoreReplies,
    fetchNextPage: fetchMoreReplies,
    isFetchingNextPage: isLoadingMoreReplies,
    error: errorRepliesData,
  } = useInfiniteQuery(portfolioChatQueries.listChatRepliesInfinite({ chatId: chat.id }));
  const addChatReplyMutation = portfolioChatMutations.useAddChatReply();

  const { chatReplies } = useChatReplies({
    chatId: chat.id,
    repliesData,
    signalREvent: SIGNALR_EVENTS.CHAT.NEW_PROJECT_CHAT_REPLY_RECEIVED,
  });
  const { sendReply } = useChatReplyHandler({ addChatReplyMutation });
  const leaveGroupChatMutation = portfolioChatMutations.useLeaveGroupChat();

  const handleAddChatReply = async ({ message, images, videos }: ChatReplyPayload) => {
    return await sendReply({ message, images, videos, chatId: chat.id });
  };

  const handleLeaveGroup = () => {
    leaveGroupChatMutation.mutate(chat.id, {
      onSuccess: () => onClose(),
    });
  };

  const error = errorRepliesData;
  if (error) {
    return <ErrorPage error={error} />;
  }

  return (
    <section className="flex h-full flex-col gap-space-md">
      {chat.groupChatInfo?.canLeave && (
        <Inline gap="sm" justify="end" wrap={false}>
          <Button
            data-testid="leave-group-chat-btn"
            variant="soft"
            icon={<LogOut01Icon />}
            isLoading={leaveGroupChatMutation.isPending}
            onClick={handleLeaveGroup}
          >
            {t("page.portfolio.chats.leave.group")}
          </Button>
          <Button
            type="link"
            target="_blank"
            href={routes.chats.details({
              id: chat.id,
              slug,
            })}
          >
            {t("page.portfolio.chats.group-chat-modal.btn.see-group-chat-details")}
          </Button>
        </Inline>
      )}
      <section className="flex min-h-0 flex-1 flex-col [&>div]:max-h-none! [&>div]:min-h-0 [&>div]:flex-1">
        <ChatWindow
          canSendChatReply={chat.canReply}
          chatReplies={chatReplies}
          hasMoreReplies={hasMoreReplies}
          fetchMoreReplies={fetchMoreReplies}
          isLoadingMoreReplies={isLoadingMoreReplies}
          sendReply={handleAddChatReply}
        />
      </section>
    </section>
  );
}
